import type { EraId } from '../config/eras';
import { cleanName, PLAYER_ID, SNOWFLAKE } from './protocol';

/** Signing in from the Discord Activity: what the server decides (worker/discord.ts), kept here to be tested. */

export interface DiscordUser {
  id: string;
  username: string;
  global_name?: string | null;
}

/** The name a Discord player races under: their display name, else their username (cleaned as typed names are). */
export function discordName(u: Pick<DiscordUser, 'username' | 'global_name'>): string {
  return cleanName(u.global_name || '') || cleanName(u.username) || 'Driver';
}

/**
 * The game player a Discord account signs in as: the one it has (the same on every device), or, the first time,
 * this device's own player (so whatever it raced before signing in stays theirs), unless that one is already
 * another Discord account's; then a fresh one.
 */
export function playerFor(known: string | null, device: unknown, deviceTaken: boolean, fresh: () => string): string {
  if (known) return known;
  return typeof device === 'string' && PLAYER_ID.test(device) && !deviceTaken ? device : fresh();
}

/**
 * Il paddock: the Grand Premio server on Discord, where drivers talk the races over (and where the championship
 * will be). The paper's line opens it, and grandprem.io/discord sends you to it (worker/index.ts), for posts and
 * emails. This invite never expires (into #paddock).
 */
export const PADDOCK_INVITE = 'https://discord.gg/npYtMKZ6mH';

/** What the game's bot asks of a server: View Channels, Send Messages, Embed Links, Attach Files, Read Message History (its lap posts and paper). */
export const DISCORD_BOT_PERMISSIONS = 1024 + 2048 + 16384 + 32768 + 65536;

/**
 * Discord's own screen for adding the game: to a server, without a bot, the manager choosing a channel for its
 * posts there and then (Discord makes a webhook in it for the game, handed back at `added`: worker/discordWebhooks.ts;
 * without `added`, no channel, and its posts go out as replies to people opening the game: worker/discordReplies.ts);
 * to a server with its bot (`bot`: only offered to a server that has the game and asks for it); or to your own
 * Discord apps (then it plays in any chat that allows outside apps).
 */
export function discordInstallUrl(clientId: string, where: 'server' | 'bot' | 'apps', guildId?: string, added?: string | null): string {
  const q = new URLSearchParams({
    client_id: clientId,
    integration_type: where === 'apps' ? '1' : '0',
    scope: where === 'bot' ? 'bot applications.commands' : 'applications.commands',
  });
  if (where === 'bot') q.set('permissions', String(DISCORD_BOT_PERMISSIONS));
  if (where === 'server' && added) {
    q.set('scope', 'applications.commands webhook.incoming');
    q.set('response_type', 'code');
    q.set('redirect_uri', added);
  }
  // One server already picked (bringing it to a server you run): Discord's list doesn't come up.
  if (where !== 'apps' && guildId) {
    q.set('guild_id', guildId);
    q.set('disable_guild_select', 'true');
  }
  return `https://discord.com/oauth2/authorize?${q}`;
}

/**
 * Bringing the game to a server you run (inside Discord): a server you own or can manage (only they can add apps)
 * that hasn't got it yet. The card for it comes up now and then, never often (src/main.ts maybeBring).
 */
export interface BringServer {
  id: string;
  name: string;
  /** Its picture on Discord (the hash), or null: its initials instead. */
  icon: string | null;
}

/** A server in the player's own list, as Discord gives it (GET /users/@me/guilds?with_counts=true). */
export interface UserGuild {
  id: string;
  name: string;
  icon?: string | null;
  owner?: boolean;
  /** Their permissions there (a number, as text). */
  permissions?: string;
  approximate_member_count?: number;
}

/** Discord's Administrator and Manage Server permissions: either lets you add apps. */
const ADMINISTRATOR = 1n << 3n;
const MANAGE_GUILD = 1n << 5n;
const ICON = /^(a_)?[0-9a-f]{32}$/;
/** Servers offered at most (one card at a time: the first not done with). */
export const BRING_MAX = 3;

function canManage(g: UserGuild): boolean {
  if (g.owner === true) return true;
  try {
    const p = BigInt(g.permissions ?? '0');
    return (p & (ADMINISTRATOR | MANAGE_GUILD)) !== 0n;
  } catch {
    return false;
  }
}

/**
 * The servers a player could bring the game to: theirs to manage, the game not in them (`inGame`: the servers
 * its bot is in), the ones they own first, then the biggest.
 */
export function bringable(mine: readonly UserGuild[], inGame: ReadonlySet<string>): BringServer[] {
  return mine
    .filter((g) => /^\d{5,25}$/.test(g.id) && typeof g.name === 'string' && g.name.trim() !== '' && !inGame.has(g.id) && canManage(g))
    .sort((a, b) => Number(b.owner === true) - Number(a.owner === true) || (b.approximate_member_count ?? 0) - (a.approximate_member_count ?? 0) || (a.name < b.name ? -1 : 1))
    .slice(0, BRING_MAX)
    .map((g) => ({ id: g.id, name: g.name.trim().slice(0, 100), icon: typeof g.icon === 'string' && ICON.test(g.icon) ? g.icon : null }));
}

/** The servers as the game hears them from its server (checked: it's only data). */
export function bringList(v: unknown): BringServer[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((s): s is BringServer => !!s && typeof s === 'object' && /^\d{5,25}$/.test((s as BringServer).id) && typeof (s as BringServer).name === 'string' && (s as BringServer).name.trim() !== '')
    .slice(0, BRING_MAX)
    .map((s) => ({ id: s.id, name: s.name.trim().slice(0, 100), icon: typeof s.icon === 'string' && ICON.test(s.icon) ? s.icon : null }));
}

/** The card comes up at most once in this long (whichever server), and for one server at most this many times. */
export const BRING_EVERY_MS = 7 * 24 * 3600 * 1000;
export const BRING_TIMES = 3;

/** When the card last came up, and how many times for each server (this browser's: gp.bring). */
export interface BringSeen {
  last: number;
  times: Record<string, number>;
}

export function bringSeen(raw: string | null): BringSeen {
  try {
    const v = raw ? (JSON.parse(raw) as Partial<BringSeen>) : null;
    const times: Record<string, number> = {};
    for (const [id, n] of Object.entries(v?.times ?? {})) if (/^\d{5,25}$/.test(id) && Number.isInteger(n) && n > 0) times[id] = n;
    return { last: typeof v?.last === 'number' && Number.isFinite(v.last) ? v.last : 0, times };
  } catch {
    return { last: 0, times: {} };
  }
}

/** The server whose card is due now, or null: not a week since the last one, or every server shown enough. */
export function bringDue(servers: readonly BringServer[], seen: BringSeen, now: number): BringServer | null {
  if (now - seen.last < BRING_EVERY_MS) return null;
  return servers.find((s) => (seen.times[s.id] ?? 0) < BRING_TIMES) ?? null;
}

/** The card came up for this server. */
export function bringShown(seen: BringSeen, id: string, now: number): BringSeen {
  return { last: now, times: { ...seen.times, [id]: (seen.times[id] ?? 0) + 1 } };
}

/** Discord's messages: each player's best lap of the day, posted when they leave (worker/discordPosts.ts). */

/** A name or server name as plain text in a message: Discord's formatting characters escaped, on one line. */
export function plainText(s: string): string {
  return s
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[\\*_~`|>#[\]()<@:]/g, '\\$&');
}

/** A lap's message: "**Sara** · 36.500 at Torrebianca · P2 in Dolce Vita Racing" (its place worked out as it posts). */
export function lapLine(o: { name: string; time: string; circuit: string; place: number | null; server: string; champion: boolean }): string {
  const parts = [`**${plainText(o.name)}**`, `${o.time} at ${plainText(o.circuit)}`];
  if (o.place !== null) parts.push(`P${o.place} in ${plainText(o.server)}`);
  if (o.champion) parts.push('yesterday’s winner');
  return parts.join(' · ');
}

/** A medal a lap earned, as the game names them. */
export type Medal = 'bronze' | 'silver' | 'gold' | 'author';

/**
 * What a lap message's picture shows, worked out by the server as the message goes out (src/lapcard.ts draws it in
 * Cloudflare's headless browser, with the game's own card code).
 */
export interface LapCardFacts {
  date: string;
  era: EraId;
  name: string;
  lapMs: number;
  /** Each sector's time, and whether it's the fastest in the server today (purple, as the game marks the day's best). */
  sectors: { ms: number; fastest: boolean }[];
  medal: Medal | null;
  /** "circuito di torrebianca · the ground effect 2022" */
  where: string;
  /** Yesterday's winner: their car is gold. */
  gold: boolean;
  /** Their place among the server's players on the lap's board, as it posts. */
  place: { rank: number; of: number } | null;
}

/** Which of a lap's sectors are the fastest among the server's laps (`others`: theirs, each three sectors; ties count). */
export function fastestSectors(own: readonly number[], others: readonly (readonly number[])[]): boolean[] {
  return own.map((ms, i) => others.every((o) => !(o[i] < ms)));
}

/** The best medal a lap time earns, or null. */
export function medalFor(lapMs: number, medals: Record<Medal, number>): Medal | null {
  return (['author', 'gold', 'silver', 'bronze'] as const).find((m) => lapMs <= medals[m]) ?? null;
}

const MEDAL_EMOJI: Record<Medal, string> = { author: '🏆', gold: '🥇', silver: '🥈', bronze: '🥉' };

/**
 * A lap message as a card, for when its picture can't be drawn (the browser busy or out of minutes): the same
 * facts in Discord's own embed, with a ready-made picture of the day's car beside them.
 *   CarWock’s best today · 34.311
 *   🥇 gold medal · P1 of 3
 *   S1 11.204 🟪 · S2 12.002 🟪 · S3 11.105
 *   circuito di torrebianca · the ground effect 2022
 */
export function lapCard(f: LapCardFacts, time: (ms: number) => string): { title: string; description: string; footer: string } {
  const head = [f.medal ? `${MEDAL_EMOJI[f.medal]} ${f.medal} medal` : '', f.place ? `P${f.place.rank} of ${f.place.of}` : ''].filter(Boolean).join(' · ');
  const sectors = f.sectors.map((s, i) => `S${i + 1} ${time(s.ms)}${s.fastest ? ' 🟪' : ''}`).join(' · ');
  return {
    title: `${f.name}’s best today · ${time(f.lapMs)}`,
    description: [head, sectors].filter(Boolean).join('\n'),
    footer: f.where,
  };
}

/** The ready-made picture of a car for that card (public/cards/cars, scripts/card-parts.mjs). */
export function carPicture(site: string, era: EraId, gold: boolean): string {
  return new URL(`cards/cars/${era}${gold ? '-gold' : ''}.png`, site.endsWith('/') ? site : `${site}/`).toString();
}

/**
 * A lap message's buttons: "Play today" (the game, your own race) and, when there's a lap to race, "Race <name>'s
 * ghost". Two, so a tap that only wants to play doesn't land on someone's ghost.
 */
export function lapButtons(name: string, linkId: string | null): { type: 1; components: { type: 2; style: 1 | 2; label: string; custom_id: string }[] } {
  const play = { type: 2 as const, style: 1 as const, label: '▶ Play today', custom_id: raceButtonId(null) };
  if (!linkId) return { type: 1, components: [play] };
  const who = name.trim() ? `${name.trim()}’s` : 'their';
  return { type: 1, components: [play, { type: 2, style: 2, label: `Race ${who} ghost`.slice(0, 80), custom_id: raceButtonId(linkId) }] };
}

/**
 * A server the game was just added to, from Discord's "app authorized" event (null: anything else, or added to
 * someone's own apps). `bot`: with its bot (false: its commands only, so its messages go out as replies).
 */
export function addedServer(event: unknown): { id: string; name: string | null; systemChannel: string | null; bot: boolean } | null {
  const e = event as {
    type?: unknown;
    event?: { type?: unknown; data?: { integration_type?: unknown; scopes?: unknown; guild?: { id?: unknown; name?: unknown; system_channel_id?: unknown } } };
  } | null;
  if (!e || e.type !== 1 || e.event?.type !== 'APPLICATION_AUTHORIZED') return null;
  const d = e.event.data;
  const g = d?.guild;
  if (d?.integration_type !== 0 || !g || typeof g.id !== 'string' || !SNOWFLAKE.test(g.id)) return null;
  return {
    id: g.id,
    name: typeof g.name === 'string' ? g.name.slice(0, 100) : null,
    systemChannel: typeof g.system_channel_id === 'string' && SNOWFLAKE.test(g.system_channel_id) ? g.system_channel_id : null,
    // Discord lists what was granted; without the list, the bot as ever.
    bot: !Array.isArray(d.scopes) || d.scopes.includes('bot'),
  };
}

/** The welcome's dropdown of the server's channels, where a manager chooses where the game posts. */
export const CHANNEL_PICK = 'channel';

/**
 * The game's hello in a server it was just added to: what it is, how to open it ("Play today", or Apps / Activities),
 * that friends race together, and where its posts go, with a dropdown of the server's channels for a manager to
 * choose another (`channel`: where they go now, or null). With the two screens' picture (`siteUrl`'s). Posted by
 * the webhook made for it (`hook`): its posts go in this channel, moved in the server's settings (no dropdown).
 */
export function welcomeMessage(siteUrl: string | null, channel: string | null, hook = false): Record<string, unknown> {
  const content = [
    '🏁 **Grand Premio** is here. A new circuit and a new car every day, your fastest lap counts, and this server gets its own leaderboard.',
    '**To play:** tap **▶ Play today** below, or start it from Apps or Activities in any chat or voice channel.',
    '**Race together:** when friends play at the same time, you see each other’s cars on the track, live.',
    hook
      ? 'Best laps get posted here when players leave, with a morning paper each day. Server managers can move them to another channel in Server Settings → Integrations → Grand Premio.'
      : channel
        ? `Best laps get posted in <#${channel}> when players leave, with a morning paper each day. Server managers can choose another channel below.`
        : 'Best laps get posted when players leave, with a morning paper each day. Server managers: choose the channel below.',
  ].join('\n\n');
  const pick = {
    type: 8,
    custom_id: CHANNEL_PICK,
    placeholder: 'Managers: choose the channel for posts',
    // Text and announcement channels.
    channel_types: [0, 5],
    min_values: 1,
    max_values: 1,
    ...(channel ? { default_values: [{ id: channel, type: 'channel' }] } : {}),
  };
  return {
    content,
    allowed_mentions: { parse: [] },
    ...(siteUrl ? { embeds: [{ image: { url: `${siteUrl.replace(/\/$/, '')}/media/discord-both.jpg` }, color: 0xb8352b }] } : {}),
    components: hook ? [lapButtons('', null)] : [{ type: 1, components: [pick] }, lapButtons('', null)],
  };
}

/** Where Discord's add screen comes back to, as the game hears it from its server (checked: it's only data), or null. */
export function addedFrom(v: unknown): string | null {
  return typeof v === 'string' && /^https:\/\/[^\s?#]+\/api\/discord\/added$/.test(v) ? v : null;
}

/** The webhook Discord made for the game as a server added it (its add screen's answer), or null: none, or not one of ours. */
export function addedHook(reply: unknown, clientId: string): { id: string; token: string; channel: string; guild: string } | null {
  const w = (reply as { webhook?: { id?: unknown; token?: unknown; channel_id?: unknown; guild_id?: unknown; application_id?: unknown } } | null)?.webhook;
  if (!w || typeof w.token !== 'string' || !w.token || w.token.length > 200 || /[^\w-]/.test(w.token)) return null;
  if (![w.id, w.channel_id, w.guild_id].every((x) => typeof x === 'string' && SNOWFLAKE.test(x)) || (w.application_id != null && w.application_id !== clientId)) return null;
  return { id: w.id as string, token: w.token, channel: w.channel_id as string, guild: w.guild_id as string };
}

/** The channel a manager chose in the welcome's dropdown, or null. */
export function pickedChannel(data: unknown): string | null {
  const d = data as { custom_id?: unknown; component_type?: unknown; values?: unknown } | undefined;
  if (!d || d.custom_id !== CHANNEL_PICK || d.component_type !== 8 || !Array.isArray(d.values)) return null;
  const v = d.values[0];
  return typeof v === 'string' && SNOWFLAKE.test(v) ? v : null;
}

/** The race button's id: the driver's own link travels in it ("race:<link>"; plain "race" opens today). */
export function raceButtonId(linkId: string | null): string {
  return linkId ? `race:${linkId}` : 'race';
}

/** A button's id read back: the link it races (null: just today), or undefined when it isn't one of ours. */
export function raceLinkOf(customId: unknown): string | null | undefined {
  if (customId === 'race') return null;
  const m = typeof customId === 'string' ? /^race:([A-Za-z0-9]{10})$/.exec(customId) : null;
  return m ? m[1] : undefined;
}

/** The morning paper's "Read the paper" button: the tapper's game opens on their own paper. */
export const PAPER_BUTTON = 'paper';

/** A lap finished just before midnight may arrive up to 10 minutes after it: the paper waits for it. */
const PAPER_FROM_MS = 10 * 60 * 1000;
/** After noon a paper about yesterday is old news: a server owed one only now (added, or a player who signed in later) waits for the next. */
const PAPER_UNTIL_MS = 12 * 3600 * 1000;

/** Whether the morning paper goes out now, this long after midnight in Rome: from 00:10 until noon. */
export function paperHours(sinceMidnightMs: number): boolean {
  return sinceMidnightMs >= PAPER_FROM_MS && sinceMidnightMs < PAPER_UNTIL_MS;
}

/**
 * A server without the bot gets its paper as a reply to the first one to open the game there that day: from 00:10
 * until the day is out (its people may only come in the evening).
 */
export function paperReplyHours(sinceMidnightMs: number): boolean {
  return sinceMidnightMs >= PAPER_FROM_MS;
}

/** "thursday 8 october" as a message writes it: "Thursday 8 October". */
const titled = (s: string): string => s.replace(/(^|\s)\p{Ll}/gu, (c) => c.toUpperCase());

/**
 * The morning paper's message, over its front page: "📰 **La Gazzetta del Grand Premio** · Thursday 8 October",
 * then "**Merky** wins at San Fruttuoso for Dolce Vita Racing · today: Circuito di Torrebianca".
 */
export function paperMessage(o: { day: string; winner: string | null; circuit: string; server: string; today: string }): string {
  const news = o.winner ? `**${plainText(o.winner)}** wins at ${plainText(o.circuit)} for ${plainText(o.server)}` : `${plainText(o.server)} raced at ${plainText(o.circuit)}`;
  return `📰 **La Gazzetta del Grand Premio** · ${titled(o.day)}\n${news} · today: ${plainText(o.today)}`;
}

/**
 * A quiet server's paper (one driver yesterday): the same news as a short card, no front page to draw.
 * "**Luca** raced San Fruttuoso alone yesterday · 30.112", the whole race's winner, today's race at the foot.
 */
export function paperCard(o: {
  day: string;
  driver: { name: string; time: string };
  circuit: string;
  overall: { name: string; time: string; total: number; ours: boolean };
  today: string;
}): { title: string; description: string; footer: string } {
  const who = `**${plainText(o.driver.name)}**`;
  const at = plainText(o.circuit);
  const n = `${o.overall.total} driver${o.overall.total === 1 ? '' : 's'}`;
  const description =
    o.overall.ours && o.overall.total === 1
      ? `${who} had ${at} to themselves yesterday · ${o.driver.time}`
      : o.overall.ours
        ? `${who} won at ${at} yesterday · ${o.driver.time}, the fastest of ${n}`
        : `${who} raced ${at} alone yesterday · ${o.driver.time}\nOverall: ${plainText(o.overall.name)} won in ${o.overall.time}, ${n}`;
  return { title: `📰 La Gazzetta del Grand Premio · ${titled(o.day)}`, description, footer: `Today: ${o.today} · closes at midnight` };
}

/** A member who may pick the server's paper channel: Administrator or Manage Server (Discord's permission bits). */
export function mayManage(permissions: unknown): boolean {
  if (typeof permissions !== 'string' || !/^\d{1,30}$/.test(permissions)) return false;
  const p = BigInt(permissions);
  return (p & 0x8n) !== 0n || (p & 0x20n) !== 0n;
}

/** A waiting best goes out: nothing posted yet that day, or it's a faster lap than the one posted. */
export function shouldPost(lapMs: number, postedMs: number | null): boolean {
  return postedMs === null || lapMs < postedMs;
}

function hexBytes(hex: string): Uint8Array<ArrayBuffer> | null {
  if (!/^(?:[0-9a-f]{2})+$/i.test(hex)) return null;
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/**
 * Whether a request really comes from Discord: it signs every interaction it sends (Ed25519, over the timestamp
 * and the body as sent) with the app's key; the app's public key checks it. Discord tests this when the address
 * is saved, and keeps sending bad signatures now and then to make sure they're refused.
 */
export async function verifyInteraction(publicKey: string, signature: string | null, timestamp: string | null, body: string): Promise<boolean> {
  const key = hexBytes(publicKey);
  const sig = signature ? hexBytes(signature) : null;
  if (!key || key.length !== 32 || !sig || sig.length !== 64 || !timestamp) return false;
  try {
    const k = await crypto.subtle.importKey('raw', key, { name: 'Ed25519' }, false, ['verify']);
    return await crypto.subtle.verify({ name: 'Ed25519' }, k, sig, new TextEncoder().encode(timestamp + body));
  } catch {
    return false;
  }
}

/** Bytes as base64 (a waiting picture is stored as text: the database hands binary back as a list of numbers). */
export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export function fromBase64(b64: string): Uint8Array<ArrayBuffer> {
  const from = (Uint8Array as unknown as { fromBase64?: (s: string) => Uint8Array<ArrayBuffer> }).fromBase64;
  if (from) return from(b64);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
